import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
	Text,
	Filter,
	Modal,
	Button,
	ColorV2,
	Pagination,
} from "@adoptaunabuelo/react-components";
import moment from "moment";
import { X, Download, ChevronRight } from "lucide-react";
import { LetterScheme, UserScheme, User } from "client-v2";
import animation from "@assets/animations/button-loading.json";
import TableGenericApp from "../tables/TableGenericApp";
import { Player } from "@lottiefiles/react-lottie-player";
import LetterNamesAnimation from "./LetterNamesAnimation";
import html2pdf from "html2pdf.js";
import { useSelector } from "react-redux";
import { State } from "@redux/index";
import letterHTML from "@assets/letter/letter";
import stamp_retro from "@assets/letter/stamp_2025.png";

const MainContainer = styled.div`
	background-color: ${ColorV2.surface.invert};
	min-width: auto;
	max-height: calc(100vh - 56px - 24px - 28px);
	overflow: auto;
	@media (min-width: 768px) {
		border-radius: 12px;
		max-height: calc(100vh - 72px - 24px - 42px);
	}
`;

const ScrollableContainer = styled.div``;

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 8px 16px;
	@media (min-width: 768px) {
		padding: 12px 16px;
		justify-content: flex-start;
		flex-direction: row;
		align-items: center;
	}
`;

const FilterContainer = styled.div`
	display: flex;
	gap: 8px;
`;

const ModalHeader = styled.div`
	background-color: #fff;
	position: sticky;
	top: 0;
	z-index: 10;
`;

const ModalContainer = styled.div`
	height: 70%;
`;

const ModalButton = styled.div`
	background-color: #fff;
	display: flex;
	justify-content: center;
	align-items: center;
	position: sticky;
	bottom: 0;
	z-index: 15;
	padding: 16px 0px;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
`;

const Divider = styled.div`
	width: 40px;
	height: 4px;
	background-color: #ccc;
	border-radius: 2px;
	margin-bottom: 6px;
	margin-top: 16px;
	margin-left: auto;
	margin-right: auto;
`;

const TopRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const PageContainer = styled.div`
	display: flex;
	align-items: center;
	background-color: ${ColorV2.surface.background};
	justify-content: center;
	padding: 12px 16px;

	@media (max-width: 768px) {
		background-color: ${ColorV2.surface.invert};
	}
`;

const LoadingView = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(0, 0, 0, 0.7);
	z-index: 10000;
`;

const DateAndLetterContainer = styled.div<{ isOptionSelected: boolean }>`
	display: flex;
	flex-direction: ${({ isOptionSelected }) =>
		isOptionSelected ? "row" : "column"};
	${({ isOptionSelected }) =>
		isOptionSelected &&
		`
		justify-content: space-between;
		width: 100%;
	`}
`;

const ColumnsAppContainer = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	flex-direction: row;
`;

const DateAppContainer = styled.div`
	display: flex;
	align-items: center;
`;

const ChevronContainer = styled.div`
	display: flex;
	align-items: center;
`;

function formatDate(createdAt: any): string | null {
	if (!createdAt) {
		console.error("createdAt no encontrado o no válido");
		return null;
	}

	let dateString;

	if (typeof createdAt === "object" && createdAt.iso) {
		dateString = createdAt.iso;
	} else if (typeof createdAt === "string") {
		dateString = createdAt;
	} else {
		console.error("Tipo inesperado de createdAt:", createdAt);
		return null;
	}

	const dateObject = new Date(dateString);

	if (!isNaN(dateObject.getTime())) {
		const day = String(dateObject.getDate()).padStart(2, "0");
		const month = String(dateObject.getMonth() + 1).padStart(2, "0");
		const year = dateObject.getFullYear();

		return `${day}/${month}/${year}`;
	} else {
		console.error("Fecha inválida:", createdAt);
		return null;
	}
}

const LettersTable: React.FC<Props> = (props) => {
	const isDesktop = useSelector((state: State) => state.screen.isDesktop);
	const [showModal, setShowModal] = useState(false);
	const [selectedLetter, setSelectedLetter] = useState<LetterScheme>();
	const [filteredGrandpas, setFilteredGrandpas] = useState<LetterScheme[]>(
		props.letters
	);
	const [sortedGrandpas, setSortedGrandpas] = useState<LetterScheme[]>([]);
	const [loadingPDF, setLoadingPDF] = useState(false);
	const grandpaFilterRef = useRef<any>(null);
	const [isOptionSelected, setIsOptionSelected] = useState(false);
	const rowsPerPage = 50;
	const totalPages = Math.ceil(props.count / rowsPerPage);
	const [currentPage, setCurrentPage] = useState(props.page || 1);
	const dateFilterRef = useRef<any>(null);
	const [sortOrderByDate, setSortOrderByDate] = useState<"recent" | "oldest">(
		"recent"
	);

	const letterTableFilters = useSelector(
		(state: State) => state.letterTableFilters
	);

	const currentUser = useSelector((state: State) => state.currentUser);

	const handlePageChange = (newPageIndex: number) => {
		const newPage = newPageIndex + 1;
		setCurrentPage(newPage);
		props.onFiltersChange?.({
			...letterTableFilters,
			page: newPage,
		});
	};

	const [selectedOptions, setSelectedOptions] = useState<
		Array<{ id: string; [key: string]: string }>
	>([]);

	const [selectedDateOptions, setSelectedDateOptions] = useState<{
		startDate: moment.Moment | null;
		endDate: moment.Moment | null;
	}>({
		startDate: null,
		endDate: null,
	});

	const [grandpaOptions, setGrandpaOptions] = useState<
		{ id: string; label: string; idGrandpa: string }[]
	>([]);

	const handleDateFilterChange = (selectedOptions: {
		startDate: moment.Moment | null;
		endDate: moment.Moment | null;
	}) => {
		setSelectedDateOptions(selectedOptions);

		props.onFiltersChange &&
			props.onFiltersChange({
				...letterTableFilters,
				createdAt: {
					min: selectedOptions.startDate?.format("YYYY-MM-DD"),
					max: selectedOptions.endDate?.format("YYYY-MM-DD") || null,
				},
				page: 1,
			});
	};

	const handleDefaultFilterChange = (
		options: Array<{ id: string; [key: string]: string }>
	) => {
		setIsOptionSelected(options.length > 0);
		setSelectedOptions(options);

		if (options.length > 0) {
			setFilteredGrandpas(
				props.letters.filter((letter) =>
					options
						.map((option) => option.label)
						.includes(letter.Grandpa?.name || "")
				)
			);

			props.onFiltersChange &&
				props.onFiltersChange({
					...letterTableFilters,
					grandpaId: options[0].idGrandpa,
					page: 1,
				});
		} else {
			setFilteredGrandpas(props.letters);
			props.onFiltersChange &&
				props.onFiltersChange({
					...letterTableFilters,
					grandpaId: null,
					page: 1,
				});
		}
	};

	useEffect(() => {
		if (!currentUser?.Residence?.objectId) return;

		User.get({
			type: "grandpa",
			residenceId: currentUser.Residence.objectId,
			state: ["inRegister", "registered", "match", "waiting"],
			data: { letter: true },
			limit: 500,
		})
			.then(({ data }) => {
				const unique = new Map<string, any>();
				data.forEach((g: any) => {
					if (!unique.has(g.objectId)) {
						unique.set(g.objectId, {
							id: `grandpa-${g.objectId}`,
							label: `${g.name ?? ""} ${g.surname ?? ""}`,
							idGrandpa: g.objectId,
						});
					}
				});
				setGrandpaOptions(Array.from(unique.values()));
			})
			.catch(console.error);
	}, [currentUser?.Residence?.objectId]);

	const getLetterTimestamp = (letter: LetterScheme) => {
		const value =
			typeof letter.createdAt === "object"
				? letter.createdAt.iso
				: letter.createdAt;
		return new Date(value).getTime();
	};

	const handleSortByDate = () => {
		const newOrder = sortOrderByDate === "recent" ? "oldest" : "recent";
		setSortOrderByDate(newOrder);

		const listToSort = [...filteredGrandpas].sort((a, b) => {
			const diff = getLetterTimestamp(a) - getLetterTimestamp(b);
			return newOrder === "recent" ? -diff : diff;
		});

		setSortedGrandpas(listToSort);
	};

	const filterByProfile = useCallback(() => {
		if (currentPage > totalPages) {
			setCurrentPage(1);
		}

		const createdAtFilter = {
			min: selectedDateOptions.startDate?.format("YYYY-MM-DD") || null,
			max: selectedDateOptions.endDate?.format("YYYY-MM-DD") || null,
		};

		const grandpaId = props.abueloData?.objectId ?? null;

		if (props.abueloData) {
			const { name, surname } = props.abueloData;
			const initialOption = {
				id: `grandpa-${name}-${surname}`,
				label: `${name} ${surname}`,
			};
			setSelectedOptions([initialOption]);
			setIsOptionSelected(true);
		} else {
			setFilteredGrandpas(props.letters);
		}

		if (props.onFiltersChange) {
			props.onFiltersChange({
				...letterTableFilters,
				createdAt: createdAtFilter,
				grandpaId,
				page: 1,
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const openLetter = (letter: LetterScheme) => {
		if (isDesktop) {
			props.onSelectLetter(letter);
		} else {
			setSelectedLetter(letter);
			setShowModal(true);
		}
	};

	const closeModal = () => {
		setSelectedLetter(undefined);
		setShowModal(false);
	};

	const getLetterMoment = (letter: LetterScheme) =>
		moment(
			typeof letter.createdAt === "object"
				? letter.createdAt.iso
				: letter.createdAt
		);

	const filterLetters = useCallback(() => {
		const { startDate, endDate } = selectedDateOptions;

		const selectedNames = isOptionSelected
			? selectedOptions.map((o) => o.label)
			: [];

		const filteredList = props.letters.filter((letter) => {
			const grandpaFullName = `${letter.Grandpa?.name || ""} ${
				letter.Grandpa?.surname || ""
			}`;

			const matchesGrandpa =
				!isOptionSelected || selectedNames.includes(grandpaFullName);

			const cartaDate = getLetterMoment(letter);
			const matchesDate =
				(!startDate || cartaDate.isSameOrAfter(startDate)) &&
				(!endDate || cartaDate.isSameOrBefore(endDate));

			return matchesGrandpa && matchesDate;
		});

		setFilteredGrandpas(filteredList);
	}, [props.letters, selectedOptions, selectedDateOptions, isOptionSelected]);

	useEffect(() => {
		setSortedGrandpas([...filteredGrandpas]);
	}, [filteredGrandpas]);

	useEffect(() => {
		filterByProfile();
	}, [filterByProfile]);

	useEffect(() => {
		filterLetters();
	}, [filterLetters]);

	const toBase64 = async (url: string): Promise<string> => {
		const response = await fetch(url);
		const blob = await response.blob();
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.readAsDataURL(blob);
		});
	};

	const downloadLetter = async () => {
		if (selectedLetter) {
			setLoadingPDF(true);

			let logoBase64 = "";
			if (selectedLetter.Coorporate?.logo) {
				logoBase64 = await toBase64(selectedLetter.Coorporate.logo.url);
			}

			let html = await letterHTML();

			// Replace placeholders in HTML
			html = html.replace("{{content}}", selectedLetter.content);
			html = html.replace(
				"{{name}}",
				`${selectedLetter.User.name} ${selectedLetter.User.surname}`
			);
			html = html.replace(
				"{{years}}",
				`${moment().diff(
					moment(selectedLetter.User.birthday?.iso),
					"year"
				)} años`
			);
			html = html.replace(
				/transform: scale\(([^)]+)\);/,
				`transform: scale(1);`
			);
			html = html.replace(
				`font-family: "Poppins";`,
				`font-family: "${
					selectedLetter.customization
						? selectedLetter.customization.font
						: "Poppins"
				}";`
			);

			// Replace corporate logo or stamp
			if (selectedLetter.Coorporate) {
				html = html.replace(
					"{{corporate_logo}}",
					`<img src="${logoBase64}" />`
				);
			} else {
				html = html.replace("{{stamp_img}}", stamp_retro);
				html = html.replace("{{corporate_logo}}", "");
			}

			// Replace other customization options
			if (selectedLetter.customization?.stamp) {
				html = html.replace("stamp-aua hide", "stamp-aua show");
			} else {
				html = html.replace("stamp-aua show", "stamp-aua hide");
			}
			if (selectedLetter.customization?.personalInfo) {
				html = html.replace("header hide", "header show");
			} else {
				html = html.replace("header show", "header hide");
			}
			if (selectedLetter.image) {
				html = html.replace(
					"{{user-image}}",
					`<div class="aua__page-break"></div> <div class="letter-image"><img src="${selectedLetter.image}" /></div>`
				);
			} else {
				html = html.replace("{{user-image}}", "");
			}

			// Generate the PDF
			const tempDiv = document.createElement("div");
			tempDiv.innerHTML = html;

			html2pdf()
				.from(tempDiv)
				.set({
					filename: "carta.pdf",
					margin: [10, 12],
					pagebreak: { mode: "css" },
					jsPDF: {
						orientation: "p",
						unit: "mm",
						format: [210, 297],
					},
				})
				.save()
				.get("pdf")
				.then(function (pdf: any) {
					window.open(pdf.output("bloburl"), "_blank");
					setLoadingPDF(false);
				});
		}
	};

	const columnsLetter: any = !isDesktop
		? [
				{
					render: (letter: LetterScheme) => (
						<ColumnsAppContainer>
							<DateAndLetterContainer
								isOptionSelected={isOptionSelected}
							>
								<LetterNamesAnimation
									letter={letter}
									isOptionSelected={isOptionSelected}
								/>

								<DateAppContainer>
									<Text
										type="c1"
										weight="regular"
										style={{
											color: ColorV2.text.neutralMedium,
											marginRight: "4px",
										}}
									>
										{!isOptionSelected
											? `Enviada el ${formatDate(
													letter.createdAt
											  )}`
											: formatDate(letter.createdAt)}
									</Text>
								</DateAppContainer>
							</DateAndLetterContainer>

							<ChevronContainer>
								<ChevronRight
									size={20}
									color={ColorV2.border.neutralMedium}
								/>
							</ChevronContainer>
						</ColumnsAppContainer>
					),
				},
		  ]
		: [
				{
					header: "Remitente",
					key: "remitente",
					render: (letter: LetterScheme) => (
						<Text
							type="p"
							weight="regular"
							style={{
								color: ColorV2.text.neutralHard,
								marginRight: "8px",
							}}
						>
							{letter.User.name} {letter.User.surname}
						</Text>
					),
				},
				{
					header: "Destinatario (Abuelo)",
					key: "destinatario",
					render: (letter: LetterScheme) => (
						<Text
							type="p"
							weight="regular"
							style={{
								color: ColorV2.text.neutralHard,
								marginRight: "8px",
							}}
						>
							{letter.Grandpa?.name} {letter.Grandpa?.surname}
						</Text>
					),
				},
				{
					header: "Fecha",
					key: "fecha",
					icon: require("../../assets/images/arrow-up.png"),
					onClick: () => handleSortByDate(),
					render: (letter: LetterScheme) => (
						<Text
							type="p2"
							weight="regular"
							style={{
								color: ColorV2.text.neutralHard,
								marginRight: "4px",
							}}
						>
							{formatDate(letter.createdAt)}
						</Text>
					),
				},
		  ];

	return (
		<>
			<MainContainer>
				{props.loading && (
					<LoadingView>
						<Player
							style={{ height: 150, width: 150 }}
							loop={true}
							autoplay={true}
							src={animation}
						/>
					</LoadingView>
				)}
				<HeaderContainer>
					<TopRow>
						<Text
							type={isDesktop ? "h4" : "h1"}
							weight="semibold"
							style={{
								color: ColorV2.text.neutralHard,
								lineHeight: "36px",
							}}
						>
							{props.count}{" "}
							{props.count === 1 ? "carta" : "cartas"}
						</Text>
					</TopRow>
					<FilterContainer>
						{/* 
						<Filter
							id="GrandpaFilter"
							placeholder={
								isDesktop ? "Dest. (abuelo)" : "Abuelo"
							}
							options={
								grandpaOptions?.map((option) => ({
									...option,
									label: option.label || "",
									idGrandpa: option.idGrandpa || "",
								})) || []
							}
							shape="circle"
							position="bottom-left"
							type="single"
							selectedOptions={selectedOptions}
							onChange={handleDefaultFilterChange}
							ref={grandpaFilterRef}
						/>
						*/}
						<Filter
							id="DateFilter"
							placeholder="Fecha"
							type="date"
							position="bottom-left"
							selectedOptions={selectedDateOptions}
							onChange={handleDateFilterChange}
							ref={dateFilterRef}
						/>
					</FilterContainer>
				</HeaderContainer>
				<ScrollableContainer>
					<TableGenericApp
						columns={columnsLetter}
						data={sortedGrandpas}
						filters={[]}
						onRowClick={(letter: LetterScheme) => {
							openLetter(letter);
						}}
						showAvatar={false}
						noResultsMessage="No hay cartas que coincidan con tu búsqueda"
					/>
				</ScrollableContainer>

				{/*
				<MobileButtonContainer>
					<FixedButton
						type="b2"
						weight="medium"
						style={{
							padding: isDesktop ? "10px 20px" : "15px 20px",
							margin: isDesktop
								? "10px 20px"
								: "15px 0px 15px 0px",
							borderRadius: "30px",
							backgroundColor: ColorV2.surface.primary,
							color: ColorV2.text.invert,
						}}
					>
						<span
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: isDesktop ? "14px" : "16px",
							}}
						>
							<Download
								size={20}
								style={{ marginRight: "8px" }}
							/>
							Descargar una de cada
						</span>
						
					</FixedButton>
				</MobileButtonContainer>
				*/}
				{showModal && selectedLetter && (
					<Modal
						type="full-screen"
						isVisible={showModal}
						onClose={closeModal}
						hideHeader={true}
						hideClose={true}
						shouldCloseOnOverlayClick={true}
					>
						<>
							<ModalHeader>
								<Divider />
								<CloseButton
									onClick={closeModal}
									style={{
										paddingLeft: "0px",
										paddingTop: "14px",
									}}
								>
									<X size={24} />
								</CloseButton>
								<Text
									type="p"
									weight="regular"
									style={{
										color: ColorV2.text.neutralHard,
										marginTop: "8px",
									}}
								>
									De {selectedLetter.User.name}{" "}
									{selectedLetter.User.surname}{" "}
								</Text>
								<Text
									type="p2"
									weight="regular"
									style={{
										color: ColorV2.text.neutralMedium,
										marginBottom: "24px",
									}}
								>
									Enviada el{" "}
									{formatDate(selectedLetter.createdAt)}
								</Text>
							</ModalHeader>
							<ModalContainer>
								<Text
									type="p2"
									weight="regular"
									style={{
										color: ColorV2.text.neutralHard,
									}}
								>
									{selectedLetter.content}
								</Text>
							</ModalContainer>
							<ModalButton>
								<Button
									design="primary"
									icon={<Download size={20} />}
									style={{ width: "100%" }}
									loading={loadingPDF}
									onClick={downloadLetter}
								>
									Descargar carta
								</Button>
							</ModalButton>
						</>
					</Modal>
				)}
			</MainContainer>

			{/* Pagination Remove
			<PageContainer>
				<Pagination
					start={currentPage - 1}
					length={props.count}
					rowsPerPage={rowsPerPage}
					onChange={handlePageChange}
				/>
			</PageContainer>
			*/}
		</>
	);
};

interface Props {
	loading?: boolean;
	page: number;
	count: number;
	letters: LetterScheme[];
	onSelectLetter: (letter: LetterScheme) => void;
	abueloData: UserScheme | undefined;
	onFiltersChange: (updatedFilters: any) => void;
}

export default LettersTable;
